// Modal padrão (overlay escuro com blur + painel branco arredondado). Extraído
// de GerenciarMatriculas.js, que já tinha essa mesma implementação localmente.
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const MAX_WIDTHS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

export default function Modal({ isOpen, onClose, title, children, maxWidth = 'xl' }) {
  // Esc fecha o modal — vale pra todo modal do sistema (edição, confirmação,
  // alerta) já que todos passam por este componente único.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Fechar ao clicar fora só deve valer quando o clique COMEÇA no overlay —
  // sem isso, selecionar texto dentro do modal arrastando o mouse até passar
  // da borda (ex.: nome de uma turma) faz o mouseup cair no overlay; como o
  // clique é sintetizado no ancestral comum de mousedown/mouseup, o target
  // acaba sendo o próprio overlay mesmo sem o usuário ter clicado nele de
  // verdade — e o modal fechava sozinho no meio da seleção.
  const mousedownNoOverlay = useRef(false);

  if (!isOpen) return null;

  const resolvedMaxWidth =
    MAX_WIDTHS[maxWidth] ||
    (typeof maxWidth === 'string' && maxWidth.startsWith('max-w-')
      ? maxWidth
      : MAX_WIDTHS.xl);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
      onMouseDown={(e) => {
        mousedownNoOverlay.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (mousedownNoOverlay.current && e.target === e.currentTarget) onClose();
        mousedownNoOverlay.current = false;
      }}
    >
      <div
        className={`w-full ${resolvedMaxWidth} max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}
